import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Checkbox, FormControlLabel, Button, Paper, Grid, Card, CardContent, CardActionArea, Alert, Snackbar, CircularProgress, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText, Divider, Tooltip, IconButton, Chip, CardMedia, MenuItem, Select, FormControl, useTheme, useMediaQuery, Stack, alpha, Badge } from '@mui/material';
import { CloudUpload as CloudUploadIcon, HelpOutline as HelpIcon, CheckCircle as CheckCircleIcon, Close as CloseIcon, Category as CategoryIcon, Inventory as InventoryIcon, ArrowBack as ArrowBackIcon, Save as SaveIcon, PhotoCamera as PhotoCameraIcon, AttachMoney as AttachMoneyIcon, Add as AddIcon, Delete as DeleteIcon, Collections as CollectionsIcon, Palette as PaletteIcon, Straighten as StraightenIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { createProduct, getSupportedCurrencies } from '../productsApi';
import { fetchCategories } from '../categoriesApi';
import { useBranch } from '../BranchContext';

export default function AddProduct() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [step, setStep] = useState(1);
  const [currencyPrices, setCurrencyPrices] = useState({ YER: '' });
  const [product, setProduct] = useState({ ProductName: '', Description: '', CategoryId: '', IsActive: true, Ingredients: '' });
  const [categories, setCategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  
  const [productImageFiles, setProductImageFiles] = useState([]);
  const [productImagePreviews, setProductImagePreviews] = useState([]);

  const [productColors, setProductColors] = useState([]);
  const [currentColor, setCurrentColor] = useState({ colorName: '', colorImage: null, colorImagePreview: null });

  // ✅ NEW: Product Sizes (unlimited size names, no images)
  const [productSizes, setProductSizes] = useState([]);
  const [currentSize, setCurrentSize] = useState({ sizeName: '' });
  
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [helpOpen, setHelpOpen] = useState(false);
  const { selectedBranch } = useBranch();

  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const data = await getSupportedCurrencies();
        setCurrencies(data.Currencies || []);
      } catch (error) {
        console.error('Error loading currencies:', error);
        setCurrencies([
          { code: 'YER', symbol: 'ر.ي', name: 'ريال يمني قديم', decimals: 0 },
          { code: 'YRN', symbol: 'ر.ي.ج', name: 'ريال يمني جديد', decimals: 0 },
          { code: 'USD', symbol: '$', name: 'دولار أمريكي', decimals: 2 },
          { code: 'SAR', symbol: 'ر.س', name: 'ريال سعودي', decimals: 2 }
        ]);
      }
    };
    loadCurrencies();
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      if (!selectedBranch) return;
      try {
        const data = await fetchCategories(selectedBranch.id || selectedBranch.Id);
        const filtered = data.filter(c => {
          const name = c.categoryName || c.CategoryName || '';
          return !name.startsWith("العروض:") && !name.startsWith("مجموعة:") && name !== "Offer" && name !== "Deal";
        });
        setCategories(filtered);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
    // eslint-disable-next-line
  }, [selectedBranch]);

  const handleCategorySelect = (category) => {
    setProduct({ ...product, CategoryId: category.id || category.Id });
    setSelectedCategoryName(category.categoryName || category.CategoryName);
    setStep(2);
  };

  const handleImageChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setImageFiles(files);
      setImagePreviews(files.map(file => URL.createObjectURL(file)));
    }
  };

  const handleProductImagesChange = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).slice(0, 8);
      setProductImageFiles(files);
      setProductImagePreviews(files.map(file => URL.createObjectURL(file)));
      if (e.target.files.length > 8) {
        setNotification({ open: true, message: t('maxProductImagesWarning') || 'Maximum 8 product images allowed. First 8 selected.', severity: 'warning' });
      }
    }
  };

  const handleRemoveProductImage = (index) => {
    setProductImageFiles(productImageFiles.filter((_, i) => i !== index));
    setProductImagePreviews(productImagePreviews.filter((_, i) => i !== index));
  };

  const handleColorNameChange = (e) => setCurrentColor({ ...currentColor, colorName: e.target.value });

  const handleColorImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCurrentColor({ ...currentColor, colorImage: file, colorImagePreview: URL.createObjectURL(file) });
    }
  };

  const handleAddColor = () => {
    if (!currentColor.colorName.trim()) {
      setNotification({ open: true, message: t('colorNameRequired') || 'Please enter a color name', severity: 'warning' });
      return;
    }
    if (productColors.some(c => c.colorName.toLowerCase() === currentColor.colorName.toLowerCase())) {
      setNotification({ open: true, message: t('colorAlreadyExists') || 'This color already exists', severity: 'warning' });
      return;
    }
    setProductColors([...productColors, { ...currentColor }]);
    setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
    setNotification({ open: true, message: t('colorAdded') || `Color "${currentColor.colorName}" added successfully`, severity: 'success' });
  };

  const handleRemoveColor = (index) => {
    const removed = productColors[index];
    setProductColors(productColors.filter((_, i) => i !== index));
    setNotification({ open: true, message: t('colorRemoved') || `Color "${removed.colorName}" removed`, severity: 'info' });
  };

  // ✅ NEW: Size handlers
  const handleSizeNameChange = (e) => setCurrentSize({ sizeName: e.target.value });

  const handleAddSize = () => {
    if (!currentSize.sizeName.trim()) {
      setNotification({ open: true, message: t('sizeNameRequired') || 'Please enter a size name', severity: 'warning' });
      return;
    }
    if (productSizes.some(s => s.sizeName.toLowerCase() === currentSize.sizeName.toLowerCase())) {
      setNotification({ open: true, message: t('sizeAlreadyExists') || 'This size already exists', severity: 'warning' });
      return;
    }
    setProductSizes([...productSizes, { sizeName: currentSize.sizeName.trim() }]);
    setCurrentSize({ sizeName: '' });
    setNotification({ open: true, message: t('sizeAdded') || `Size "${currentSize.sizeName}" added successfully`, severity: 'success' });
  };

  const handleAddSizeOnEnter = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddSize();
    }
  };

  const handleRemoveSize = (index) => {
    const removed = productSizes[index];
    setProductSizes(productSizes.filter((_, i) => i !== index));
    setNotification({ open: true, message: t('sizeRemoved') || `Size "${removed.sizeName}" removed`, severity: 'info' });
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setProduct({ ...product, [name]: type === 'checkbox' ? checked : value });
  };

  const handleAddCurrency = (currency) => {
    if (!currencyPrices[currency]) setCurrencyPrices({ ...currencyPrices, [currency]: '' });
  };

  const handleRemoveCurrency = (currency) => {
    const updated = { ...currencyPrices };
    delete updated[currency];
    setCurrencyPrices(updated);
  };

  const handlePriceChange = (currency, value) => setCurrencyPrices({ ...currencyPrices, [currency]: value });

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : code;
  };

  const getCurrencyInfo = (code) => currencies.find(c => c.code === code) || { code, symbol: code, name: code, decimals: 2 };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedBranch) {
      setNotification({ open: true, message: t('selectBranchFirst'), severity: 'warning' });
      return;
    }

    const validPrices = Object.entries(currencyPrices).filter(([_, price]) => price && parseFloat(price) > 0);
    
    if (!product.ProductName || validPrices.length === 0 || !product.CategoryId) {
      setNotification({ open: true, message: t('fillRequiredFields'), severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      let imageJson = null;
      if (imageFiles.length > 0) {
        const file = imageFiles[0];
        const reader = new FileReader();
        imageJson = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
      }

      let productImagesArray = [];
      if (productImageFiles.length > 0) {
        console.log(`📸 Processing ${productImageFiles.length} product images`);
        for (const file of productImageFiles) {
          const reader = new FileReader();
          const base64 = await new Promise((resolve) => {
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(file);
          });
          productImagesArray.push(base64);
        }
        console.log(`✅ Converted ${productImagesArray.length} images to base64`);
      }

      let productColorsArray = [];
      if (productColors.length > 0) {
        console.log(`🎨 Processing ${productColors.length} product colors`);
        for (const color of productColors) {
          let colorImageBase64 = null;
          if (color.colorImage) {
            const reader = new FileReader();
            colorImageBase64 = await new Promise((resolve) => {
              reader.onload = (e) => resolve(e.target.result);
              reader.readAsDataURL(color.colorImage);
            });
          }
          productColorsArray.push({ colorName: color.colorName, colorImage: colorImageBase64 });
        }
        console.log(`✅ Processed ${productColorsArray.length} colors`);
      }

      // ✅ NEW: Build sizes array
      let productSizesArray = [];
      if (productSizes.length > 0) {
        productSizesArray = productSizes.map(s => ({ sizeName: s.sizeName }));
        console.log(`📏 Processed ${productSizesArray.length} sizes`);
      }

      const finalCurrencyPrices = {};
      validPrices.forEach(([currency, price]) => { finalCurrencyPrices[currency] = parseFloat(price); });

      const payload = {
        ProductName: product.ProductName,
        Description: product.Description,
        CurrencyPrices: finalCurrencyPrices,
        CategoryId: parseInt(product.CategoryId),
        IsActive: product.IsActive,
        Ingredients: product.Ingredients,
        ImageJson: imageJson,
        ProductImages: productImagesArray.length > 0 ? productImagesArray : null,
        ProductColors: productColorsArray.length > 0 ? productColorsArray : null,
        ProductSizes: productSizesArray.length > 0 ? productSizesArray : null, // ✅ NEW
        CreatedDate: new Date().toISOString(),
        BranchId: selectedBranch.id || selectedBranch.Id
      };

      console.log('📤 Sending payload:', {
        ...payload,
        ImageJson: imageJson ? '[BASE64 DATA]' : null,
        ProductImages: productImagesArray.length > 0 ? `[${productImagesArray.length} BASE64 IMAGES]` : null,
        ProductColors: productColorsArray.length > 0 ? `[${productColorsArray.length} COLORS]` : null,
        ProductSizes: productSizesArray.length > 0 ? `[${productSizesArray.length} SIZES]` : null // ✅ NEW
      });

      await createProduct(payload);
      setNotification({ open: true, message: t('productCreatedSuccess'), severity: 'success' });
      
      setProduct({ ProductName: '', Description: '', CategoryId: '', IsActive: true, Ingredients: '' });
      setCurrencyPrices({ YER: '' });
      setImageFiles([]);
      setImagePreviews([]);
      setProductImageFiles([]);
      setProductImagePreviews([]);
      setProductColors([]);
      setCurrentColor({ colorName: '', colorImage: null, colorImagePreview: null });
      setProductSizes([]); // ✅ NEW
      setCurrentSize({ sizeName: '' }); // ✅ NEW
      setStep(1);
    } catch (error) {
      console.error('Error creating product:', error);
      setNotification({ open: true, message: t('createProductError'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const HelpDialog = () => {
    const steps = [
      { label: t('helpAddProductStep1Label') || 'Step 1: Select Category', icon: <CategoryIcon color="primary" />, description: t('helpAddProductStep1Description') || 'Choose the category for your product', tips: [t('helpAddProductStep1Tip1') || 'Categories help organize products', t('helpAddProductStep1Tip2') || 'Click any category card to proceed', t('helpAddProductStep1Tip3') || 'You can change category later'] },
      { label: t('helpAddProductStep2Label') || 'Step 2: Product Details', icon: <InventoryIcon color="primary" />, description: t('helpAddProductStep2Description') || 'Fill in product information', tips: [t('helpAddProductStep2Tip1') || 'Name and price are required', t('helpAddProductStep2Tip2') || 'Add prices in multiple currencies', t('helpAddProductStep2Tip3') || 'Description helps customers', t('helpAddProductStep2Tip4') || 'Ingredients are optional'] },
      { label: t('helpAddProductStep3Label') || 'Step 3: Upload Images', icon: <CollectionsIcon color="primary" />, description: t('helpAddProductStep3Description') || 'Add product images (up to 8 additional images)', tips: [t('helpAddProductStep3Tip1') || 'Main image is required', t('helpAddProductStep3Tip2') || 'Add up to 8 additional product images', t('helpAddProductStep3Tip3') || 'Images are auto-optimized', t('helpAddProductStep3Tip4') || 'Preview before saving'] },
      { label: t('helpAddProductStep4Label') || 'Step 4: Save Product', icon: <SaveIcon color="success" />, description: t('helpAddProductStep4Description') || 'Review and create', tips: [t('helpAddProductStep4Tip1') || 'Double check all details', t('helpAddProductStep4Tip2') || 'Product goes live immediately', t('helpAddProductStep4Tip3') || 'You can edit later'] }
    ];

    return (
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile} sx={{ '& .MuiDialog-container': { alignItems: 'flex-start', paddingTop: { xs: '100px', sm: '80px', md: '100px' } } }} PaperProps={{ sx: { borderRadius: isMobile ? 0 : 2, maxHeight: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 160px)', md: 'calc(100vh - 200px)' }, overflowY: 'auto' } }}>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1, p: { xs: 2, sm: 2.5 } }}>
          <HelpIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{t('helpAddProductDialogTitle') || 'How to Add Products'}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={() => setHelpOpen(false)} sx={{ color: 'white' }} size={isMobile ? "small" : "medium"}>
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2, p: { xs: 2, sm: 3 } }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('helpWhatIsAddProduct') || 'What is this page?'}</Typography>
            <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>{t('helpWhatIsAddProductDesc') || 'Add new products to your menu quickly and easily.'}</Typography>
          </Alert>

          <List sx={{ width: '100%' }}>
            {steps.map((step, index) => (
              <Box key={index}>
                <ListItem alignItems="flex-start" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 0, sm: 2 } }}>
                  <ListItemIcon sx={{ mt: 1, minWidth: { xs: 40, sm: 56 } }}>
                    <Box sx={{ bgcolor: 'primary.light', borderRadius: '50%', p: { xs: 1, sm: 1.5 }, display: 'flex', boxShadow: 1 }}>
                      {React.cloneElement(step.icon, { sx: { fontSize: { xs: 18, sm: 24 } } })}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1, fontSize: { xs: '0.95rem', sm: '1.1rem' } }}>{step.label}</Typography>}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>{step.description}</Typography>
                        <Box sx={{ bgcolor: '#f5f5f5', p: { xs: 1.5, sm: 2 }, borderRadius: 1 }}>
                          <Typography variant="caption" fontWeight="bold" color="primary" sx={{ mb: 1, display: 'block', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>💡 {t('helpTips') || 'Tips'}</Typography>
                          {step.tips.map((tip, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                              <CheckCircleIcon sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: 'success.main', mt: 0.3 }} />
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>{tip}</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < steps.length - 1 && <Divider />}
              </Box>
            ))}
          </List>

          <Alert severity="warning" sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>⚠️ {t('helpImportantNotes') || 'Important Notes'}</Typography>
            <Typography variant="body2" component="div" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
              <Box sx={{ mt: 1 }}>
                • {t('helpAddProductImportantNote1') || 'At least one currency price required'}<br/>
                • {t('helpAddProductImportantNote2') || 'Main image recommended'}<br/>
                • {t('helpAddProductImportantNote3') || 'Up to 5 additional product images'}<br/>
                • {t('helpAddProductImportantNote4') || 'Products go live immediately when saved'}
              </Box>
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2, sm: 2.5 }, bgcolor: '#f9f9f9' }}>
          <Button onClick={() => setHelpOpen(false)} variant="contained" color="primary" fullWidth={isMobile} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
            {t('helpGotIt') || 'Got it!'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
      <HelpDialog />

      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={{ xs: 2, sm: 0 }} mb={{ xs: 2.5, sm: 3 }}>
        <Stack direction="row" alignItems="center" spacing={{ xs: 1.5, sm: 2 }}>
          <InventoryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
              {step === 1 ? t('addProduct') : (isMobile ? selectedCategoryName : `${t('addProductTitle')} ${selectedCategoryName}`)}
            </Typography>
            {step === 2 && !isMobile && (
              <Chip label={selectedCategoryName} color="primary" icon={<CategoryIcon sx={{ fontSize: 16 }} />} onDelete={() => setStep(1)} size="small" sx={{ mt: 1 }} />
            )}
          </Box>
        </Stack>
        <Tooltip title={t('helpButtonTooltip') || 'Click for help'} arrow>
          <Button variant="contained" color="info" startIcon={<HelpIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} onClick={() => setHelpOpen(true)} fullWidth={isMobile} size={isMobile ? "medium" : "large"} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', fontSize: { xs: '0.85rem', sm: '0.9rem' }, boxShadow: 3, '&:hover': { boxShadow: 6 } }}>
            {t('helpButtonText') || 'Help'}
          </Button>
        </Tooltip>
      </Stack>

      {!selectedBranch && (
        <Alert severity="warning" sx={{ mb: 3 }}>{t('selectBranchToAddProduct') || 'Please select a branch first'}</Alert>
      )}

      {step === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('selectCategoryHelp') || 'Select a category to get started'}</Typography>
          </Alert>
          
          {categories.length === 0 ? (
            <Alert severity="warning">{t('noCategoriesFound') || 'No categories found. Please add categories first.'}</Alert>
          ) : (
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {categories.map((category) => (
                <Grid item xs={6} sm={4} md={3} key={category.id || category.Id}>
                  <Card sx={{ cursor: 'pointer', transition: 'all 0.3s ease', borderRadius: { xs: 2, sm: 3 }, '&:hover': { boxShadow: 6, transform: isMobile ? 'none' : 'translateY(-4px)' }, '&:active': { transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)' } }} onClick={() => handleCategorySelect(category)}>
                    <CardActionArea sx={{ p: { xs: 2, sm: 3 }, textAlign: 'center', minHeight: { xs: 100, sm: 120 }, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <CategoryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main', mb: 1 }} />
                      <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}>
                        {category.categoryName || category.CategoryName}
                      </Typography>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {step === 2 && (
        <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, borderRadius: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={{ xs: 2, sm: 3 }}>
            <IconButton onClick={() => setStep(1)} size={isMobile ? "small" : "medium"}>
              <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton>
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>{t('productDetails') || 'Product Details'}</Typography>
            {isMobile && <Chip label={selectedCategoryName} color="primary" size="small" sx={{ ml: 'auto', fontSize: '0.7rem' }} />}
          </Stack>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('productName')} *</Typography>
                <TextField fullWidth name="ProductName" value={product.ProductName} onChange={handleChange} required placeholder={t('enterProductName') || 'Enter product name'} variant="outlined" size={isMobile ? "small" : "medium"} sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.9rem', sm: '1rem' } } }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                  {t('prices')} * ({Object.keys(currencyPrices).length} {t('currencies') || 'currencies'})
                </Typography>
                <Alert severity="info" sx={{ py: { xs: 0.5, sm: 1 }, '& .MuiAlert-message': { fontSize: { xs: '0.75rem', sm: '0.85rem' } } }}>
                  💡 {t('addPricesInMultipleCurrencies') || 'Add prices in multiple currencies'}
                </Alert>
              </Grid>

              {Object.keys(currencyPrices).map((currency) => {
                const currInfo = getCurrencyInfo(currency);
                return (
                  <Grid item xs={12} sm={6} md={4} key={currency}>
                    <Paper elevation={1} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: '#f9f9f9', borderLeft: '4px solid', borderColor: 'primary.main' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Chip label={`${currInfo.symbol} ${currency}`} color="primary" size="small" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, height: { xs: 22, sm: 24 } }} />
                        {Object.keys(currencyPrices).length > 1 && (
                          <IconButton size="small" color="error" onClick={() => handleRemoveCurrency(currency)}>
                            <DeleteIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                          </IconButton>
                        )}
                      </Box>
                      <TextField fullWidth type="number" value={currencyPrices[currency]} onChange={(e) => handlePriceChange(currency, e.target.value)} placeholder={`السعر بـ  ${currency}`} inputProps={{ step: currInfo.decimals === 0 ? "1" : "0.01", min: "0" }} InputProps={{ startAdornment: (<InputAdornment position="start"><Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{currInfo.symbol}</Typography></InputAdornment>) }} size="small" sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '0.9rem' } } }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{currInfo.name}</Typography>
                    </Paper>
                  </Grid>
                );
              })}

              <Grid item xs={12}>
                <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                  <Select value="" onChange={(e) => handleAddCurrency(e.target.value)} displayEmpty startAdornment={<InputAdornment position="start"><AddIcon sx={{ color: 'primary.main', fontSize: { xs: 18, sm: 20 } }} /></InputAdornment>} sx={{ '& .MuiSelect-select': { fontSize: { xs: '0.85rem', sm: '0.9rem' } } }}>
                    <MenuItem value="" disabled><em>➕ {t('addAnotherCurrency') || 'Add Another Currency'}</em></MenuItem>
                    {currencies.filter(c => !currencyPrices[c.code]).map((curr) => (
                      <MenuItem key={curr.code} value={curr.code}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{curr.symbol}</Typography>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>{curr.name} ({curr.code})</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('description')}</Typography>
                <TextField fullWidth name="Description" value={product.Description} onChange={handleChange} multiline rows={isMobile ? 2 : 3} placeholder={t('enterDescription') || 'Enter product description'} variant="outlined" size={isMobile ? "small" : "medium"} sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '0.9rem' } } }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('ingredients')}</Typography>
                <TextField fullWidth name="Ingredients" value={product.Ingredients} onChange={handleChange} placeholder={t('enterIngredients') || 'e.g., Flour, Sugar, Eggs'} variant="outlined" size={isMobile ? "small" : "medium"} sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '0.9rem' } } }} />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('status')}</Typography>
                <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: alpha(theme.palette.success.main, 0.05), border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}>
                  <FormControlLabel control={<Checkbox checked={product.IsActive} onChange={handleChange} name="IsActive" color="primary" size={isMobile ? "small" : "medium"} />} label={<Typography sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('isActive') || 'Product is Active'}</Typography>} />
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('mainProductImage') || 'Main Product Image'}</Typography>
                <Box sx={{ border: '2px dashed', borderColor: imagePreviews.length > 0 ? 'success.main' : 'divider', p: { xs: 2, sm: 3 }, borderRadius: 2, textAlign: 'center', bgcolor: imagePreviews.length > 0 ? alpha(theme.palette.success.main, 0.05) : '#fafafa', transition: 'all 0.3s' }}>
                  <input accept="image/*" style={{ display: 'none' }} id="main-image-upload" type="file" onChange={handleImageChange} />
                  <label htmlFor="main-image-upload">
                    <Button variant="contained" component="span" startIcon={<PhotoCameraIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} color={imagePreviews.length > 0 ? 'success' : 'primary'} size={isMobile ? "medium" : "large"} fullWidth={isMobile} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, mb: imagePreviews.length > 0 ? { xs: 1.5, sm: 2 } : 0 }}>
                      {imagePreviews.length > 0 ? `✓ ${t('mainImageSelected') || 'Main Image Selected'}` : t('uploadMainImage') || 'Upload Main Image'}
                    </Button>
                  </label>
                  
                  {imagePreviews.length > 0 && (
                    <Card sx={{ width: { xs: '100%', sm: 200 }, maxWidth: 300, height: { xs: 200, sm: 200 }, boxShadow: 3, mx: 'auto' }}>
                      <CardMedia component="img" image={imagePreviews[0]} alt="Main Product Image" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Card>
                  )}
                  
                  {imagePreviews.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{t('uploadMainImageHint') || 'JPG, PNG, WebP • Max 5MB'}</Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>{t('additionalProductImages') || 'Additional Product Images'}</Typography>
                  <Chip label={`${productImagePreviews.length}/8`} size="small" color={productImagePreviews.length >= 8 ? 'error' : 'primary'} sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                  <Chip label={t('optional') || 'Optional'} size="small" variant="outlined" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                </Stack>
                
                <Alert severity="info" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                  📸 {t('productImagesHint') || 'Upload up to 8 additional product images. These will be displayed in a gallery.'}
                </Alert>

                <Box sx={{ border: '2px dashed', borderColor: productImagePreviews.length > 0 ? 'info.main' : 'divider', p: { xs: 2, sm: 3 }, borderRadius: 2, textAlign: 'center', bgcolor: productImagePreviews.length > 0 ? alpha(theme.palette.info.main, 0.05) : '#fafafa', transition: 'all 0.3s' }}>
                  <input accept="image/*" style={{ display: 'none' }} id="product-images-upload" multiple type="file" onChange={handleProductImagesChange} disabled={productImagePreviews.length >= 8} />
                  <label htmlFor="product-images-upload">
                    <Button variant="contained" component="span" startIcon={<CollectionsIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} color={productImagePreviews.length > 0 ? 'info' : 'primary'} size={isMobile ? "medium" : "large"} fullWidth={isMobile} disabled={productImagePreviews.length >= 8} sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' }, mb: productImagePreviews.length > 0 ? { xs: 2, sm: 2.5 } : 0 }}>
                      {productImagePreviews.length >= 8 ? `✓ ${t('maxImagesReached') || 'Maximum 8 Images Reached'}` : productImagePreviews.length > 0 ? `✓ ${productImagePreviews.length} ${t('imagesSelected') || 'Images Selected'}` : t('uploadProductImages') || 'Upload Product Images (Up to 8)'}
                    </Button>
                  </label>
                  
                  {productImagePreviews.length > 0 && (
                    <Grid container spacing={2}>
                      {productImagePreviews.map((src, index) => (
                        <Grid item xs={6} sm={4} md={2.4} key={index}>
                          <Card sx={{ position: 'relative', boxShadow: 3 }}>
                            <Badge badgeContent={<IconButton size="small" sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' }, width: 24, height: 24 }} onClick={() => handleRemoveProductImage(index)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>} anchorOrigin={{ vertical: 'top', horizontal: 'right' }} sx={{ width: '100%', '& .MuiBadge-badge': { top: 8, right: 8 } }}>
                              <CardMedia component="img" image={src} alt={`Product Image ${index + 1}`} sx={{ width: '100%', height: { xs: 120, sm: 150 }, objectFit: 'cover' }} />
                            </Badge>
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.6)', color: 'white', p: 0.5, textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{t('image') || 'Image'} {index + 1}</Typography>
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                  
                  {productImagePreviews.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>{t('uploadProductImagesHint') || 'Select multiple images (Max 8) • JPG, PNG, WebP • Max 5MB each'}</Typography>
                  )}
                </Box>
              </Grid>

              {/* ===== Product Colors Section ===== */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>🎨 {t('productColors') || 'Product Colors'}</Typography>
                  <Chip label={`${productColors.length} ${t('colors') || 'colors'}`} size="small" color="secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                  <Chip label={t('optional') || 'Optional'} size="small" variant="outlined" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                </Stack>
                
                <Alert severity="info" sx={{ mb: 3, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                  🎨 {t('colorsHint') || 'Add color variants for this product. Each color can have its own image. Colors will be displayed as options for customers.'}
                </Alert>

                <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 3, bgcolor: alpha(theme.palette.secondary.main, 0.05), border: `2px dashed ${theme.palette.secondary.main}` }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 2 }}>➕ {t('addNewColor') || 'Add New Color'}</Typography>
                  
                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, mb: 0.5, display: 'block' }}>{t('colorName') || 'Color Name'} *</Typography>
                      <TextField fullWidth value={currentColor.colorName} onChange={handleColorNameChange} placeholder={t('enterColorName') || 'e.g., Red, Blue, Black'} variant="outlined" size={isMobile ? "small" : "medium"} sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '0.9rem' } } }} />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, mb: 0.5, display: 'block' }}>{t('colorImage') || 'Color Image'} ({t('optional') || 'Optional'})</Typography>
                      <input accept="image/*" style={{ display: 'none' }} id="color-image-upload" type="file" onChange={handleColorImageChange} />
                      <label htmlFor="color-image-upload">
                        <Button variant="outlined" component="span" fullWidth startIcon={<PhotoCameraIcon />} size={isMobile ? "small" : "medium"} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                          {currentColor.colorImagePreview ? `✓ ${t('imageSelected') || 'Image Selected'}` : t('uploadImage') || 'Upload Image'}
                        </Button>
                      </label>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Button variant="contained" color="secondary" onClick={handleAddColor} fullWidth startIcon={<AddIcon />} size={isMobile ? "small" : "medium"} disabled={!currentColor.colorName.trim()} sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                        {t('addColor') || 'Add Color'}
                      </Button>
                    </Grid>

                    {currentColor.colorImagePreview && (
                      <Grid item xs={12}>
                        <Card sx={{ maxWidth: 200, boxShadow: 2 }}>
                          <CardMedia component="img" image={currentColor.colorImagePreview} alt={currentColor.colorName} sx={{ height: 120, objectFit: 'cover' }} />
                          <Box sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.6)', textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ color: 'white', fontSize: '0.7rem' }}>{t('preview') || 'Preview'}</Typography>
                          </Box>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                </Paper>

                {productColors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 2 }}>📋 {t('addedColors') || 'Added Colors'} ({productColors.length})</Typography>
                    
                    <Grid container spacing={2}>
                      {productColors.map((color, index) => (
                        <Grid item xs={6} sm={4} md={3} key={index}>
                          <Card sx={{ position: 'relative', boxShadow: 3, border: `2px solid ${theme.palette.secondary.main}`, transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}>
                            <IconButton size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'error.main', color: 'white', zIndex: 1, '&:hover': { bgcolor: 'error.dark' }, width: 28, height: 28 }} onClick={() => handleRemoveColor(index)}>
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </IconButton>

                            {color.colorImagePreview ? (
                              <CardMedia component="img" image={color.colorImagePreview} alt={color.colorName} sx={{ width: '100%', height: { xs: 120, sm: 150 }, objectFit: 'cover' }} />
                            ) : (
                              <Box sx={{ width: '100%', height: { xs: 120, sm: 150 }, bgcolor: alpha(theme.palette.secondary.main, 0.1), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PhotoCameraIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: alpha(theme.palette.secondary.main, 0.3) }} />
                              </Box>
                            )}

                            <CardContent sx={{ p: { xs: 1, sm: 1.5 }, bgcolor: 'secondary.main' }}>
                              <Typography variant="body2" fontWeight="bold" align="center" sx={{ color: 'white', fontSize: { xs: '0.8rem', sm: '0.9rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                🎨 {color.colorName}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}
                
                {productColors.length === 0 && (
                  <Alert severity="info" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    ℹ️ {t('noColorsAdded') || 'No colors added yet. Add colors above to give customers color options.'}
                  </Alert>
                )}
              </Grid>

              {/* ✅ NEW: Product Sizes Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 3 }} />
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>📏 {t('productSizes') || 'Product Sizes'}</Typography>
                  <Chip label={`${productSizes.length} ${t('sizes') || 'sizes'}`} size="small" color="warning" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                  <Chip label={t('optional') || 'Optional'} size="small" variant="outlined" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }} />
                </Stack>

                <Alert severity="info" sx={{ mb: 3, fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                  📏 {t('sizesHint') || 'Add size options for this product (e.g., S, M, L, XL or 39, 40, 41). Customers will be able to select a size.'}
                </Alert>

                <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, mb: 3, bgcolor: alpha(theme.palette.warning.main, 0.05), border: `2px dashed ${theme.palette.warning.main}` }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 2 }}>➕ {t('addNewSize') || 'Add New Size'}</Typography>

                  <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={12} sm={8}>
                      <Typography variant="caption" fontWeight="bold" sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, mb: 0.5, display: 'block' }}>{t('sizeName') || 'Size Name'} *</Typography>
                      <TextField fullWidth value={currentSize.sizeName} onChange={handleSizeNameChange} onKeyDown={handleAddSizeOnEnter} placeholder={t('enterSizeName') || 'e.g., S, M, L, XL or 39, 40, 41'} variant="outlined" size={isMobile ? "small" : "medium"} InputProps={{ startAdornment: (<InputAdornment position="start"><StraightenIcon sx={{ color: 'warning.main', fontSize: { xs: 18, sm: 20 } }} /></InputAdornment>) }} sx={{ '& .MuiInputBase-input': { fontSize: { xs: '0.85rem', sm: '0.9rem' } } }} />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Button variant="contained" color="warning" onClick={handleAddSize} fullWidth startIcon={<AddIcon />} size={isMobile ? "small" : "medium"} disabled={!currentSize.sizeName.trim()} sx={{ fontWeight: 'bold', fontSize: { xs: '0.8rem', sm: '0.9rem' } }}>
                        {t('addSize') || 'Add Size'}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>

                {productSizes.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, mb: 2 }}>📋 {t('addedSizes') || 'Added Sizes'} ({productSizes.length})</Typography>

                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {productSizes.map((size, index) => (
                        <Chip
                          key={index}
                          label={`📏 ${size.sizeName}`}
                          onDelete={() => handleRemoveSize(index)}
                          deleteIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                          color="warning"
                          variant="filled"
                          sx={{
                            fontSize: { xs: '0.85rem', sm: '0.95rem' },
                            fontWeight: 'bold',
                            py: { xs: 2, sm: 2.5 },
                            px: 1,
                            color: 'white',
                            '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.9)', '&:hover': { color: 'white' } }
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {productSizes.length === 0 && (
                  <Alert severity="info" sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                    ℹ️ {t('noSizesAdded') || 'No sizes added yet. Add sizes above to give customers size options.'}
                  </Alert>
                )}
              </Grid>

              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1, sm: 2 } }}>
                  <Button type="submit" variant="contained" color="success" size={isMobile ? "medium" : "large"} disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />} fullWidth={isMobile} sx={{ px: { xs: 3, sm: 4 }, py: { xs: 1.2, sm: 1.5 }, fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' }, boxShadow: 3, '&:hover': { boxShadow: 6 } }}>
                    {loading ? t('creating') : t('createProduct') || 'Create Product'}
                  </Button>
                  <Button variant="outlined" color="error" size={isMobile ? "medium" : "large"} onClick={() => setStep(1)} fullWidth={isMobile} sx={{ px: { xs: 3, sm: 4 }, fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                    {t('cancel') || 'Cancel'}
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      <Snackbar open={notification.open} autoHideDuration={6000} onClose={() => setNotification({ ...notification, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }} sx={{ '& .MuiSnackbarContent-root': { minWidth: isMobile ? '90vw' : 'auto' } }}>
        <Alert onClose={() => setNotification({ ...notification, open: false })} severity={notification.severity} sx={{ width: '100%', fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}